
import { CreateElement } from "vue";
import Component, { mixins } from "vue-class-component";
import { ProvideReactive } from "vue-property-decorator";
import { EVENT_NAMES } from "@scrile/api-provider";
import { CallCreateResponse } from "@scrile/api-provider/dist/api/CallsProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { ErrorDTO } from "@scrile/api-provider/dist/lib/apiErrorHandler";
import WithAuthUser from "@/mixins/WithAuthUser";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { callMethod, callRenderFunction } from "@/lib/vueClassHelpers";
import { NotificationData } from "@/types";
import { EVENTS } from "@/const";
// eslint-disable-next-line @typescript-eslint/ban-ts-ignore
// @ts-ignore
// noinspection TypeScriptCheckImport
import BaseButton from "@/components/BaseButton";
import BaseConfirm from "@/components/BaseConfirm";
import CallController from "@/components/CallController/CallController";
import CallControllerWithRender from "@/components/CallController?original=true";
import PaidActionConfirm from "@/components/PaidActionConfirm";
import PaidActionError from "@/components/PaidActionError";
import WithPaidCalls from "@/plugins/paidCalls/mixins/WithPaidCalls";

// inject: [] - temporary bug fix for ProvideReactive: https://github.com/kaorun343/vue-property-decorator/issues/277
@Component({ name: "PaidCallController", components: { BaseConfirm, BaseButton }, inject: [] })
export default class PaidCallController extends mixins(CallController, WithPaidCalls, WithAuthUser) {
  public showPaidConfirm = false;

  public showRefillBalance = false;

  @ProvideReactive("paidCallPrice")
  public callPrice: number = 0;

  public refillBalance() {
    this.showRefillBalance = false;
    emitter.emit(EVENTS.BALANCE_REFILL_SHOW);
  }

  public callCreate(data: { receiverId: string }) {
    providers.PaidCallsProvider.setReceiveErrors(false);
    const callPromise = callMethod(this, CallController, "callCreate", [data]) as Promise<CallCreateResponse>;
    callPromise.catch((e: ErrorDTO[]) => {
      e.forEach(e => {
        if (e.details?.messageTemplate === "{com.scrile.paid_calls.validation.PaidCallCreateValidator.message}") {
          this.showRefillBalance = true;
        } else {
          emitter.emit(EVENT_NAMES.REQUEST_STATUS, e);
        }
      });
    });
    return callPromise;
  }

  public setPaidChatAnswer(status: boolean) {
    this.showPaidConfirm = false;
    if (status) {
      callMethod(this, CallController, "callOutgoingHandler", [{ opponentUser: this.opponentUser }]);
    } else {
      this.reset();
    }
  }

  public async getCallPrice(userId: string) {
    const price = await this.paidCallsFindCallPrice(userId);
    this.callPrice = price.perMinutePricePackage?.amount ?? 0;
  }

  public async callOutgoingHandler({ opponentUser }: { opponentUser: UserPublicData }) {
    await this.getCallPrice(opponentUser.id);
    if (this.callPrice === 0) {
      callMethod(this, CallController, "callOutgoingHandler", [{ opponentUser }]);
    } else {
      this.opponentUser = opponentUser;
      this.showPaidConfirm = true;
    }
  }

  public async callIncomingHandler(...args: any[]) {
    if (this.call) return;
    await this.getCallPrice(this.authUserStrict.id);
    return callMethod(this, CallController, "callIncomingHandler", args);
  }

  public render(h: CreateElement) {
    if (this.showPaidConfirm) {
      return h(PaidActionConfirm, {
        props: { value: this.showPaidConfirm, price: this.callPrice },
        on: { input: this.setPaidChatAnswer },
        scopedSlots: {
          default: ({ price }: { price: string }) => $t("Call for {0} per min", [price]),
        },
      });
    }
    return h("div", { staticStyle: { position: "absolute" } }, [
      callRenderFunction(this, CallControllerWithRender, h),
      h(PaidActionError, {
        props: { value: this.showRefillBalance },
        on: {
          input: (v: boolean) => {
            this.showRefillBalance = v;
            this.reset(true);
          },
        },
      }),
    ]);
  }

  public reset(...args: any[]) {
    this.callPrice = 0;
    return callMethod(this, CallController, "reset", args);
  }

  public async created() {
    const paidCallEventListener = await this.paidCallsSubscribeRequestPaymentForCall(async callId => {
      if (this.call && callId === this.call.id) {
        const notification: NotificationData = {
          text: $t("1 min left! Please refill"),
          position: "top",
          timeout: 60 * 1000,
          onNotificationClick: () => this.refillBalance(),
        };
        emitter.emit(EVENTS.NOTIFICATION_SHOW, notification);
      }
    });
    this.$once("hook:beforeDestroy", () => paidCallEventListener.unsubscribe());
  }
}
